import { Color } from "@repo/app-types/constants/colors";
import type { ConfigurationOperation } from "@repo/app-types/entities/configurationAgent";
import {
  outcomeGreenRedYellowResultCategoryColor,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  getOperationCategory,
  getOperationLabel,
  isKnowledgeBaseDocumentOperation,
  isTestCaseOperation,
} from "@repo/app-types/helpers/configurationAgentParser";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { getPillColor, Pill, type PillColor } from "@repo/ui/Pills/pill";
import {
  IconCheck,
  IconCircleMinus,
  IconCirclePlus,
  IconFileText,
  IconFlask,
  IconPencil,
} from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "../../../components/recordingAssessmentEditor/fieldDiffView.js";
import { useConfigurationAssistantContext } from "./configurationAssistantContext.js";

export interface PlaybookOperationCardProps {
  /** Stable ID for tracking applied state across re-renders */
  operationId: string;
  operation: ConfigurationOperation;
}

export const PlaybookOperationCard: FC<PlaybookOperationCardProps> = ({
  operationId,
  operation,
}) => {
  const {
    applyOperation,
    isOperationApplied,
    getEntityName,
    getTestSpec,
    outcomeDefinition,
    getDocumentDisplayName,
  } = useConfigurationAssistantContext();
  const [isLoading, setIsLoading] = useState(false);

  const category = getOperationCategory(operation);
  const label = getOperationLabel(operation);
  const isApplied = isOperationApplied(operationId);
  const isTestCase = isTestCaseOperation(operation);
  const isKbDocument = isKnowledgeBaseDocumentOperation(operation);

  const handleApply = async () => {
    setIsLoading(true);
    try {
      await applyOperation(operationId, operation);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={twMerge(
        "overflow-hidden rounded-xl border border-border-light bg-white shadow-xs",
        category === "add" && "border-emerald-200",
        category === "update" && "border-blue-200",
        category === "remove" && "border-rose-200",
      )}
    >
      {/* Header */}
      <div
        className={twMerge(
          "flex items-center gap-2 px-3 py-2",
          category === "add" && "bg-emerald-50",
          category === "update" && "bg-blue-50",
          category === "remove" && "bg-rose-50",
        )}
      >
        <OperationIcon
          category={category}
          isTestCase={isTestCase}
          isKnowledgeBaseDocument={isKbDocument}
        />
        <span
          className={twMerge(
            "text-xs font-semibold",
            category === "add" && "text-emerald-700",
            category === "update" && "text-blue-700",
            category === "remove" && "text-rose-700",
          )}
        >
          {label}
        </span>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5">
        <OperationDetails
          operation={operation}
          category={category}
          getEntityName={getEntityName}
          getTestSpec={getTestSpec}
          getDocumentDisplayName={getDocumentDisplayName}
          outcomeDefinition={outcomeDefinition}
        />
      </div>

      {/* Apply button */}
      <div className="border-t border-gray-100 px-3 py-2">
        <button
          type="button"
          onClick={handleApply}
          disabled={isApplied || isLoading}
          className={twMerge(
            "flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-colors",
            isApplied || isLoading
              ? "cursor-default bg-gray-100 text-muted-foreground"
              : twMerge(
                  category === "add" &&
                    "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
                  category === "update" &&
                    "bg-blue-100 text-blue-700 hover:bg-blue-200",
                  category === "remove" &&
                    "bg-rose-100 text-rose-700 hover:bg-rose-200",
                ),
          )}
        >
          {isLoading ? (
            <>
              <Loader
                style={LoaderStyle.SPINNER}
                className="size-3.5 fill-gray-500"
              />
              {getLoadingButtonLabel(operation.type)}
            </>
          ) : isApplied ? (
            <>
              <IconCheck className="size-3.5" />
              {getAppliedButtonLabel(operation.type)}
            </>
          ) : (
            getButtonLabel(operation.type)
          )}
        </button>
      </div>
    </div>
  );
};

const OperationIcon: FC<{
  category: "add" | "update" | "remove";
  isTestCase?: boolean;
  isKnowledgeBaseDocument?: boolean;
}> = ({ category, isTestCase, isKnowledgeBaseDocument }) => {
  const iconClass = twMerge(
    "size-3.5",
    category === "add" && "text-emerald-600",
    category === "update" && "text-blue-600",
    category === "remove" && "text-rose-600",
  );

  if (isTestCase) {
    return <IconFlask className={iconClass} />;
  }

  if (isKnowledgeBaseDocument) {
    return <IconFileText className={iconClass} />;
  }

  switch (category) {
    case "add":
      return <IconCirclePlus className={iconClass} />;
    case "update":
      return <IconPencil className={iconClass} />;
    case "remove":
      return <IconCircleMinus className={iconClass} />;
  }
};

const OperationDetails: FC<{
  operation: ConfigurationOperation;
  category: "add" | "update" | "remove";
  getEntityName: (
    id: string,
    type: "section" | "criteria" | "test-case",
  ) => string | undefined;
  getTestSpec: (
    id: string,
  ) =>
    | { name: string; description: string; expectedOutcome?: string }
    | undefined;
  getDocumentDisplayName: (slugifiedName: string) => string | undefined;
  outcomeDefinition: OutcomeDefinition | undefined;
}> = ({
  operation,
  category,
  getEntityName,
  getTestSpec,
  getDocumentDisplayName,
  outcomeDefinition,
}) => {
  const fields = getOperationFields(
    operation,
    getEntityName,
    getTestSpec,
    getDocumentDisplayName,
  );

  // Helper to get pill color and label from outcome value
  const getOutcomePillProps = (
    outcomeValue: string,
  ): { label: string; color: PillColor } => {
    if (!outcomeDefinition) {
      return { label: outcomeValue, color: Color.Gray };
    }

    // Find the result that matches this value
    const result = outcomeDefinition.results.find(
      (r) => r.value === outcomeValue,
    );
    if (!result) {
      return { label: outcomeValue, color: Color.Gray };
    }

    // Use the same pattern as outcomeSettingsForm.tsx
    const color = outcomeGreenRedYellowResultCategoryColor[result.category];
    return {
      label: result.label,
      color: getPillColor(color) ?? Color.Gray,
    };
  };

  return (
    <div className="space-y-2.5">
      {fields.map((field, idx) => (
        <div key={idx}>
          <span className="text-[10px] font-medium text-muted-foreground">
            {field.label}
          </span>
          {field.type === "badge" ? (
            <div className="mt-0.5">
              <Badge category={category}>{field.value}</Badge>
            </div>
          ) : field.type === "outcome" ? (
            <div className="mt-0.5">
              <Pill
                label={getOutcomePillProps(field.value).label}
                size={Size.SMALL}
                color={getOutcomePillProps(field.value).color}
              />
            </div>
          ) : field.type === "diff" ? (
            <FieldDiffView
              className="mt-0.5 max-h-64 overflow-y-auto text-[13px] leading-relaxed"
              savedValue={field.baseline ?? ""}
              editedValue={field.value}
            />
          ) : (
            <p
              className={twMerge(
                "mt-0.5 text-[13px] leading-relaxed whitespace-pre-wrap text-foreground",
                category === "remove" && "line-through",
              )}
            >
              {field.value}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

interface FieldItem {
  label: string;
  value: string;
  type: "text" | "badge" | "outcome" | "diff";
  /** Baseline value for a "diff" field (the current content to diff against). */
  baseline?: string;
}

type EntityNameLookup = (
  id: string,
  type: "section" | "criteria" | "test-case",
) => string | undefined;

type TestSpecLookup = (
  id: string,
) =>
  | { name: string; description: string; expectedOutcome?: string }
  | undefined;

type DocumentDisplayNameLookup = (slugifiedName: string) => string | undefined;

/**
 * Get all fields for an operation with their labels
 */
function getOperationFields(
  operation: ConfigurationOperation,
  getEntityName: EntityNameLookup,
  getTestSpec: TestSpecLookup,
  getDocumentDisplayName: DocumentDisplayNameLookup,
): FieldItem[] {
  const fields: FieldItem[] = [];

  switch (operation.type) {
    case "remove-section":
      fields.push({
        label: "Section name",
        value: operation.name,
        type: "text",
      });
      break;

    case "remove-criteria":
      fields.push({
        label: "Criteria name",
        value: operation.name,
        type: "text",
      });
      break;

    case "add-test-case":
      fields.push({
        label: "Test case name",
        value: operation.name,
        type: "text",
      });
      fields.push({
        label: "Customer behavior",
        value: operation.description,
        type: "text",
      });
      if (operation.expectedOutcome) {
        fields.push({
          label: "Expected outcome",
          value: operation.expectedOutcome,
          type: "outcome",
        });
      }
      break;

    case "update-test-case": {
      const currentTestSpec = getTestSpec(operation.id);
      const testCaseName =
        currentTestSpec?.name ?? getEntityName(operation.id, "test-case");

      // Always show which test case is being updated
      if (testCaseName) {
        fields.push({
          label: "Updating",
          value: testCaseName,
          type: "badge",
        });
      }

      // Only show fields that are actually changing
      if (operation.name && operation.name !== currentTestSpec?.name) {
        fields.push({
          label: "New name",
          value: operation.name,
          type: "text",
        });
      }
      if (
        operation.description &&
        operation.description !== currentTestSpec?.description
      ) {
        fields.push({
          label: "Customer behavior",
          value: operation.description,
          type: "text",
        });
      }
      if (
        operation.expectedOutcome &&
        operation.expectedOutcome !== currentTestSpec?.expectedOutcome
      ) {
        fields.push({
          label: "Expected outcome",
          value: operation.expectedOutcome,
          type: "outcome",
        });
      }
      break;
    }

    case "remove-test-case":
      fields.push({
        label: "Test case name",
        value: operation.name,
        type: "text",
      });
      break;

    case "update-knowledge-base-document":
      fields.push({
        label: "Article",
        value:
          getDocumentDisplayName(operation.documentName) ??
          operation.documentName,
        type: "badge",
      });
      fields.push({
        label: "Current text",
        value: operation.patch.find,
        type: "text",
      });
      fields.push({
        label: "New text",
        value: operation.patch.replace,
        type: "text",
      });
      break;
  }

  return fields;
}

function getButtonLabel(operationType: ConfigurationOperation["type"]): string {
  switch (operationType) {
    case "add-test-case":
      return "Create test case";
    case "update-test-case":
      return "Update test case";
    case "remove-test-case":
      return "Remove test case";
    case "update-knowledge-base-document":
      return "Apply changes";
    default:
      return "Apply change";
  }
}

function getLoadingButtonLabel(
  operationType: ConfigurationOperation["type"],
): string {
  switch (operationType) {
    case "add-test-case":
      return "Creating...";
    case "update-test-case":
      return "Updating...";
    case "remove-test-case":
      return "Removing...";
    case "update-knowledge-base-document":
      return "Applying...";
    default:
      return "Applying...";
  }
}

function getAppliedButtonLabel(
  operationType: ConfigurationOperation["type"],
): string {
  switch (operationType) {
    case "add-test-case":
      return "Test case created";
    case "update-test-case":
      return "Test case updated";
    case "remove-test-case":
      return "Test case removed";
    case "update-knowledge-base-document":
      return "Changes applied";
    default:
      return "Change applied";
  }
}

const Badge: FC<{
  category: "add" | "update" | "remove";
  children: string;
}> = ({ category, children }) => (
  <span
    className={twMerge(
      "inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium",
      category === "add" && "bg-emerald-100 text-emerald-700",
      category === "update" && "bg-blue-100 text-blue-700",
      category === "remove" && "bg-rose-100 text-rose-700",
    )}
  >
    {children}
  </span>
);
